import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { z } from 'zod'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/src/components/ui/table'
import { ArrowCounterClockwiseIcon, EyeIcon } from '@phosphor-icons/react'
import { Input } from '@/src/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { Skeleton } from '@/src/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/src/components/ui/dialog'
import { JsonViewer } from '@/src/components/DataViewer/json-viewer'
import { listLogs } from '@/src/api/log'
import { TableStates } from '@/src/components/manage/tables/TableStates'
import { Pagination } from '@/src/components/manage/Pagination'
import { DateTimeText } from '@/src/components/ui/date-time'
import { useTranslation } from '@/src/i18n/client'
import { FilterAction } from '@/src/components/manage/actions/FilterAction'

const PAGE_SIZE = 20

const searchSchema = z.object({
  type: z.string().optional(),
  userId: z.string().optional(),
  timeframe: z.enum(['all', '1h', '24h', '7d', '30d']).optional(),
  page: z.coerce.number().int().min(1).optional(),
})

function getTimeframeDates(timeframe: string) {
  if (timeframe === 'all') return {}
  const now = new Date()
  let from: Date
  if (timeframe === '1h') from = new Date(now.getTime() - 1 * 60 * 60 * 1000)
  else if (timeframe === '24h') from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  else if (timeframe === '7d') from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  else if (timeframe === '30d') from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  else return {}
  return { from: from.toISOString(), to: now.toISOString() }
}

export const Route = createFileRoute('/_manage/manage/admin/logs')({
  component: AdminLogsPage,
  validateSearch: searchSchema,
})

function AdminLogsPage() {
  const { t } = useTranslation()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const type = searchParams.type ?? '__all__'
  const userId = searchParams.userId ?? ''
  const timeframe = searchParams.timeframe ?? '24h'
  const page = (searchParams.page ?? 1) - 1
  const setPage = (nextPage: number) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage > 0 ? nextPage + 1 : undefined,
      }),
    })
  }
  const setFilters = (updates: { type?: string; userId?: string; timeframe?: 'all' | '1h' | '24h' | '7d' | '30d' }) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        type: updates.type === undefined ? prev.type : (updates.type === '__all__' ? undefined : updates.type),
        userId: updates.userId === undefined ? prev.userId : (updates.userId || undefined),
        timeframe: updates.timeframe === undefined ? prev.timeframe : (updates.timeframe === '24h' ? undefined : updates.timeframe),
        page: undefined,
      }),
    })
  }
  const [openLog, setOpenLog] = useState<any>(null)

  const TIMEFRAMES = useMemo(() => [
    { label: t('table.timeframe_all'), value: 'all' },
    { label: t('table.timeframe_1h'), value: '1h' },
    { label: t('table.timeframe_24h'), value: '24h' },
    { label: t('table.timeframe_7d'), value: '7d' },
    { label: t('table.timeframe_30d'), value: '30d' },
  ], [t])

  const queryParams = useMemo(() => {
    const { from, to } = getTimeframeDates(timeframe)
    return {
      type: type === '__all__' ? undefined : type,
      userId: userId || undefined,
      from,
      to,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }
  }, [type, userId, timeframe, page])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['log', 'list', queryParams],
    queryFn: () => listLogs({ data: queryParams }),
    staleTime: 1000 * 30,
  })

  const logs = useMemo(() => (data?.logs ?? []).map((log: any) => ({
    ...log,
    createdAt: typeof log.createdAt === 'string' ? log.createdAt : log.createdAt?.toISOString?.() ?? '',
  })), [data])

  const typeOptions = useMemo(() => Array.from(
    new Set([...(logs.map((l: any) => l.type)), type].filter(Boolean))
  ), [logs, type])

  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            aria-label="Refresh"
          >
            Refresh
          </Button>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            compact
          />
          <FilterAction>
            <div className="flex flex-col gap-3">
              <Select value={timeframe} onValueChange={v => { if (v) setFilters({ timeframe: v as 'all' | '1h' | '24h' | '7d' | '30d' }) }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEFRAMES.map(tf => (
                    <SelectItem key={tf.value} value={tf.value}>{tf.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={type} onValueChange={v => { if (v) setFilters({ type: v }) }}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Types</SelectItem>
                  {typeOptions.filter((itemType: string) => itemType !== '__all__').map((itemType: string) => (
                    <SelectItem key={itemType} value={itemType}>{itemType}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="User ID"
                value={userId}
                onChange={e => { setFilters({ userId: e.target.value }) }}
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        type: undefined,
                        userId: undefined,
                        timeframe: undefined,
                        page: undefined,
                      }),
                    })
                  }}
                  aria-label="Reset Filters"
                >
                  <ArrowCounterClockwiseIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </FilterAction>
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%]">Date</TableHead>
              <TableHead className="w-[12%]">Type</TableHead>
              <TableHead className="w-[15%]">User</TableHead>
              <TableHead className="w-[15%]">API Key</TableHead>
              <TableHead className="w-[33%]">Details</TableHead>
              <TableHead className="text-right w-[10%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableStates
              isLoading={isLoading}
              error={error}
              data={logs}
              colSpan={6}
              emptyMessage="No logs found."
              renderRow={(log: any) => (
                <TableRow key={log.id}>
                  <TableCell><DateTimeText>{log.createdAt}</DateTimeText></TableCell>
                  <TableCell>{log.type}</TableCell>
                  <TableCell>{log.userId ?? '-'}</TableCell>
                  <TableCell>{log.apiKey || '-'}</TableCell>
                  <TableCell style={{ maxHeight: 64, overflow: 'hidden' }}>
                    <pre className="whitespace-pre-wrap max-w-xs overflow-x-auto text-xs" style={{ maxHeight: 64, overflow: 'auto' }}>
                      {typeof log.details === 'string'
                        ? log.details
                        : JSON.stringify(log.details, null, 2)}
                    </pre>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View"
                      onClick={() => setOpenLog(log)}
                      className="hover:bg-transparent focus:bg-transparent active:bg-transparent"
                      style={{ background: 'none' }}
                    >
                      <EyeIcon className="w-4 h-4" style={{ color: 'inherit' }} />
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            />
          </TableBody>
        </Table>
      </div>
      {/* Log details dialog */}
      <Dialog open={!!openLog} onOpenChange={open => !open && setOpenLog(null)}>
        <DialogContent className="w-screen md:min-w-4xl max-w-full rounded-none sm:rounded-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Log Entry Details</DialogTitle>
          </DialogHeader>
          {openLog ? (
            <div className="space-y-4 text-sm overflow-y-auto pr-2 flex-1">
              <div>
                <span className="font-semibold">Date:</span> <DateTimeText>{openLog.createdAt}</DateTimeText>
              </div>
              <div>
                <span className="font-semibold">Type:</span> {openLog.type}
              </div>
              <div>
                <span className="font-semibold">User:</span> {openLog.userId || '-'}
              </div>
              <div>
                <span className="font-semibold">API Key:</span> {openLog.apiKey || '-'}
              </div>
              <div>
                <span className="font-semibold">Details:</span>
                <div className="mt-1">
                  <JsonViewer data={typeof openLog.details === 'string' ? JSON.parse(openLog.details) : openLog.details} height={400} />
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
